import React, { createContext, useContext, useEffect, useState } from "react";
// Notice we now import `initializeKeycloak` and named `keycloak` export
import { initializeKeycloak, keycloak } from "../utils/keycloak"; 
// Import your config variables here
import { KEYCLOAK_CLIENT_ID, KEYCLOAK_REALM, KEYCLOAK_URL } from "../utils/config"; 
import { User, getUserFromToken } from "@app/model/User";

type AuthContextType = {
  initialized: boolean;
  authenticated: boolean;
  user?: User;
  keycloak: typeof keycloak;
  /**
   * Set when sign-in could not even be attempted (auth server down, bad config).
   * Without it, `initialized` simply never flips and the app hangs on a spinner.
   */
  authError: Error | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | undefined>();
  const [authError, setAuthError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const setupAuth = async () => {
      // Guard against missing configs
      if (!KEYCLOAK_REALM || !KEYCLOAK_CLIENT_ID) {
        console.error("Missing Keycloak configuration!");
        if (isMounted) setAuthError(new Error("The application is missing its sign-in configuration."));
        return;
      }

      try {
        // 1. Kick off initialization with explicit configuration
        await initializeKeycloak({
          url: KEYCLOAK_URL,
          realm: KEYCLOAK_REALM,
          clientId: KEYCLOAK_CLIENT_ID
        });

        if (isMounted) {
          setInitialized(true);
          setAuthenticated(!!keycloak.authenticated);
          setUser(getUserFromToken(keycloak.tokenParsed));

          // 2. Attach auth listeners *after* initialization
          keycloak.onAuthSuccess = () => {
            setAuthenticated(true);
            setUser(getUserFromToken(keycloak.tokenParsed));
          };

          keycloak.onAuthRefreshSuccess = () => {
            setUser(getUserFromToken(keycloak.tokenParsed));
          };

          keycloak.onAuthLogout = () => {
            setAuthenticated(false);
            setUser(undefined);
          };
        }
      } catch (error) {
        console.error("Keycloak initialization failed", error);
        if (isMounted) {
          setAuthError(new Error("The sign-in service could not be reached, so the application cannot start."));
        }
      }
    };

    setupAuth();

    return () => {
      isMounted = false; // Cleanup to prevent state updates if unmounted
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        initialized,
        authenticated,
        user,
        keycloak,
        authError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}